import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Upload, FileSpreadsheet, ScanLine, Play, Undo2, Loader2,
  AlertTriangle, CheckCircle2, History, Download,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../ui/dialog";
import { API, BACKEND_URL } from "../../config/api";

const fmt = (n) => (n ?? 0).toLocaleString("en-IN");
const fmtPct = (n, d) => (d > 0 ? `${Math.round((n / d) * 100)}%` : "—");

export default function TaskImportCard() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [job, setJob] = useState(null);
  const [polling, setPolling] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [showJobs, setShowJobs] = useState(false);
  const [showApplyConfirm, setShowApplyConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [showRollbackConfirm, setShowRollbackConfirm] = useState(null);
  const [rollbackText, setRollbackText] = useState("");
  const pollRef = useRef(null);

  const loadJobs = async () => {
    try {
      const res = await axios.get(`${API}/admin/task-import/jobs?limit=20`);
      setJobs(res.data.jobs || []);
    } catch {}
  };
  useEffect(() => { loadJobs(); }, []);

  const refreshJob = async (jid) => {
    try {
      const res = await axios.get(`${API}/admin/task-import/status?job_id=${jid}`);
      setJob(res.data);
      if (["completed", "failed"].includes(res.data.status)) {
        setPolling(false);
        loadJobs();
      }
    } catch (e) {
      setPolling(false);
    }
  };

  // Poll while running
  useEffect(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (polling && job?.job_id) {
      pollRef.current = setInterval(() => refreshJob(job.job_id), 2000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [polling, job?.job_id]);

  const onUpload = async () => {
    if (!file) { toast.error("Choose an .xlsx first"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/admin/task-import/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded — job ${res.data.job_id}`);
      await refreshJob(res.data.job_id);
      loadJobs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const runDry = async () => {
    if (!job) return;
    try {
      await axios.post(`${API}/admin/task-import/dry-run?job_id=${job.job_id}`);
      toast.info("Dry run started — refreshes every 2 s");
      setPolling(true);
      refreshJob(job.job_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Dry-run failed"); }
  };

  const runApply = async () => {
    if (!job) return;
    setShowApplyConfirm(false); setConfirmText("");
    try {
      await axios.post(`${API}/admin/task-import/apply?job_id=${job.job_id}&confirm=CONFIRM`);
      toast.info("Apply started — refreshes every 2 s");
      setPolling(true);
      refreshJob(job.job_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Apply failed"); }
  };

  const runRollback = async (j) => {
    setShowRollbackConfirm(null); setRollbackText("");
    try {
      const res = await axios.post(`${API}/admin/task-import/rollback?job_id=${j.job_id}&confirm=ROLLBACK`);
      toast.success(`Rolled back — ${fmt(res.data.deleted)} interactions removed`);
      loadJobs();
      if (job?.job_id === j.job_id) refreshJob(j.job_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Rollback failed"); }
  };

  const sessionToken = typeof window !== "undefined"
    ? window.localStorage.getItem("propertycrm_session_token") : null;

  const isRunning = job?.status === "running";
  const isDone = job?.status === "completed";
  const isDry = job?.mode === "dry_run";

  return (
    <Card className="border-blue-200" data-testid="task-import-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <FileSpreadsheet className="w-4 h-4 text-blue-600" />
          LeadSquared Task Trend Import
          <Badge className="bg-blue-100 text-blue-800 border-blue-200">Interactions</Badge>
        </CardTitle>
        <p className="text-xs text-slate-600 mt-1">
          Imports the <strong>Task Trend Report</strong> .xlsx as <code>interactions</code> (call/touchpoint
          logs — not work-item tasks). Matches each row to an existing lead by <strong>phone</strong>
          (10-digit normalised), falling back to <strong>email</strong>. Orphans are skipped and listed.
          Idempotent — re-uploading the same file upserts rows by <code>Opportunity Id + Task CreatedOn</code>.
          Each imported doc carries <code>source=leadsquared_task_import</code> + <code>import_job_id</code>
          so rollback removes only that batch.
        </p>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Upload */}
        <div className="flex flex-wrap items-center gap-2">
          <Input
            type="file"
            accept=".xlsx"
            data-testid="task-import-file"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="max-w-sm"
          />
          <Button
            onClick={onUpload}
            disabled={!file || uploading || isRunning}
            data-testid="task-import-upload"
            className="bg-slate-700 hover:bg-slate-800"
          >
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Upload
          </Button>
          <Button
            onClick={() => { setShowJobs(!showJobs); loadJobs(); }}
            variant="outline"
            data-testid="task-import-history"
          >
            <History className="w-4 h-4 mr-2" />
            {showJobs ? "Hide" : "Show"} jobs ({jobs.length})
          </Button>
        </div>

        {job && (
          <div className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid="task-import-current">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Badge variant="outline" className="font-mono">{job.job_id}</Badge>
              <Badge className={
                job.status === "completed" ? "bg-emerald-100 text-emerald-800" :
                job.status === "running" ? "bg-blue-100 text-blue-800" :
                job.status === "failed" ? "bg-rose-100 text-rose-800" :
                "bg-slate-100 text-slate-700"
              }>{job.status}</Badge>
              {job.mode && <Badge variant="outline" className={isDry ? "border-sky-300 text-sky-800" : "border-emerald-300 text-emerald-800"}>{job.mode}</Badge>}
              <span className="text-xs text-slate-500">{job.filename} · {fmt(job.size)} bytes</span>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                onClick={runDry}
                disabled={!job || isRunning}
                data-testid="task-import-dryrun"
                className="bg-sky-600 hover:bg-sky-700"
              >
                {isRunning && isDry ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ScanLine className="w-4 h-4 mr-2" />}
                Dry Run
              </Button>
              <Button
                onClick={() => setShowApplyConfirm(true)}
                disabled={!job || isRunning || !(isDone && isDry)}
                data-testid="task-import-apply"
                className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40"
              >
                <Play className="w-4 h-4 mr-2" />
                Apply
              </Button>
              {job.orphan > 0 && (
                <a
                  href={`${BACKEND_URL}/api/admin/task-import/orphans?job_id=${job.job_id}`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center text-xs text-amber-700 underline"
                >
                  <Download className="w-3.5 h-3.5 mr-1" /> orphan sample (.json)
                </a>
              )}
            </div>

            {/* Progress */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-sm">
              <div><span className="text-slate-500">Scanned</span><br/><strong>{fmt(job.scanned)}</strong></div>
              <div><span className="text-slate-500">Matched</span><br/><strong className="text-emerald-700">{fmt(job.matched)}</strong></div>
              <div><span className="text-slate-500">Orphan</span><br/><strong className={job.orphan > 0 ? "text-amber-700" : ""}>{fmt(job.orphan)} <span className="text-xs text-slate-500">({fmtPct(job.orphan, job.scanned)})</span></strong></div>
              <div><span className="text-slate-500">Upserted</span><br/><strong>{fmt(job.upserted)}</strong></div>
              <div><span className="text-slate-500">Modified</span><br/><strong>{fmt(job.modified)}</strong></div>
              <div><span className="text-slate-500">Errors</span><br/><strong className={job.errors > 0 ? "text-rose-700" : ""}>{fmt(job.errors)}</strong></div>
            </div>

            {job.by_project && Object.keys(job.by_project).length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-500 mt-2 mb-1">By project</p>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(job.by_project).map(([k, v]) => (
                    <Badge key={k} variant="outline">{k}: <strong className="ml-1">{fmt(v)}</strong></Badge>
                  ))}
                </div>
              </div>
            )}
            {job.by_match && Object.keys(job.by_match).length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-500 mt-2 mb-1">Matched via</p>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(job.by_match).map(([k, v]) => (
                    <Badge key={k} variant="outline" className={k === "phone" ? "border-emerald-200" : "border-amber-200"}>
                      {k}: <strong className="ml-1">{fmt(v)}</strong>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {job.by_tasktype_top?.length > 0 && (
              <details>
                <summary className="text-xs uppercase tracking-wider text-slate-500 cursor-pointer mt-2">Top task types ({job.by_tasktype_top.length})</summary>
                <div className="mt-1 text-xs space-y-0.5">
                  {job.by_tasktype_top.map(([k, v]) => (
                    <div key={k} className="flex justify-between"><span>{k}</span><strong>{fmt(v)}</strong></div>
                  ))}
                </div>
              </details>
            )}
            {job.samples?.length > 0 && (
              <details>
                <summary className="text-xs uppercase tracking-wider text-slate-500 cursor-pointer">Sample rows ({job.samples.length})</summary>
                <div className="mt-1 overflow-x-auto">
                  <table className="text-xs w-full">
                    <thead className="text-slate-500">
                      <tr><th className="text-left pr-2">Opp / Lead</th><th className="text-left pr-2">Type</th><th className="text-left pr-2">Subject</th><th className="text-left pr-2">Status</th><th className="text-left pr-2">Match</th></tr>
                    </thead>
                    <tbody>
                      {job.samples.map((s, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="pr-2 py-1">
                            <div className="font-mono text-[10px] text-slate-500">{s.opp_id}</div>
                            <div className="font-mono text-[10px]">{s.lead_id}</div>
                          </td>
                          <td className="pr-2 py-1">{s.task_type}</td>
                          <td className="pr-2 py-1">{s.subject}</td>
                          <td className="pr-2 py-1"><Badge variant="outline">{s.status}</Badge></td>
                          <td className="pr-2 py-1"><Badge variant="outline">{s.match_via}</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
            {job.status === "failed" && job.error && (
              <div className="bg-rose-50 border border-rose-200 rounded p-2 text-xs">
                <p className="font-semibold text-rose-800 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5"/> Failed</p>
                <p className="font-mono text-rose-700 mt-1">{job.error}</p>
              </div>
            )}
            {job.rolled_back && (
              <div className="text-xs text-amber-700">⤺ Rolled back: deleted {fmt(job.rolled_back_count)} interactions on {job.rolled_back_at?.slice(0, 19).replace("T", " ")} UTC.</div>
            )}
          </div>
        )}

        {showJobs && (
          <div className="border border-slate-200 rounded-lg p-3" data-testid="task-import-jobs-history">
            <p className="text-sm font-semibold text-slate-800 mb-2">Job history</p>
            {jobs.length === 0 ? (
              <p className="text-xs text-slate-500 italic">No jobs yet.</p>
            ) : (
              <div className="space-y-1">
                {jobs.map((j) => (
                  <div key={j.job_id} className="border border-slate-100 rounded p-2 text-xs flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className="font-mono">{j.job_id}</Badge>
                    <Badge className={
                      j.status === "completed" ? "bg-emerald-100 text-emerald-800" :
                      j.status === "running" ? "bg-blue-100 text-blue-800" :
                      j.status === "failed" ? "bg-rose-100 text-rose-800" :
                      "bg-slate-100 text-slate-700"
                    }>{j.status}</Badge>
                    {j.mode && <span className="text-slate-500">{j.mode}</span>}
                    <span className="text-slate-500">{j.uploaded_at?.slice(0, 16).replace("T", " ")}</span>
                    <span>matched <strong>{fmt(j.matched)}</strong> · orphan {fmt(j.orphan)}</span>
                    {j.upserted > 0 && <span>upserted <strong className="text-emerald-700">{fmt(j.upserted)}</strong></span>}
                    {j.rolled_back && <Badge variant="outline" className="text-amber-700 border-amber-300">rolled back</Badge>}
                    <Button size="sm" variant="ghost" onClick={() => refreshJob(j.job_id)}>Load</Button>
                    {j.mode === "apply" && j.status === "completed" && !j.rolled_back && j.upserted > 0 && (
                      <Button size="sm" variant="outline" onClick={() => setShowRollbackConfirm(j)} className="text-amber-700 border-amber-300 hover:bg-amber-50">
                        <Undo2 className="w-3 h-3 mr-1" /> Rollback
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>

      {/* Apply confirm */}
      <Dialog open={showApplyConfirm} onOpenChange={(o) => !o && setShowApplyConfirm(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" /> Confirm Apply
            </DialogTitle>
            <DialogDescription>
              About to upsert <strong>{fmt(job?.matched)}</strong> interactions into the live DB.
              Existing records with the same <code>(Opportunity Id, Task CreatedOn)</code> will be
              updated in place. Type <code className="bg-slate-100 px-1">CONFIRM</code>.
            </DialogDescription>
          </DialogHeader>
          <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="Type CONFIRM" data-testid="task-import-confirm-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowApplyConfirm(false); setConfirmText(""); }}>Cancel</Button>
            <Button onClick={runApply} disabled={confirmText !== "CONFIRM"} data-testid="task-import-apply-submit" className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40">Apply now</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!showRollbackConfirm} onOpenChange={(o) => !o && setShowRollbackConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Undo2 className="w-5 h-5 text-amber-600" /> Confirm Rollback</DialogTitle>
            <DialogDescription>
              Delete every interaction created by run <code>{showRollbackConfirm?.job_id}</code>
              (~<strong>{fmt(showRollbackConfirm?.upserted)}</strong> records). Only docs flagged
              <code className="mx-1">source=leadsquared_task_import</code> AND matching this job_id are removed —
              real interactions are never touched. Type <code className="bg-slate-100 px-1">ROLLBACK</code>.
            </DialogDescription>
          </DialogHeader>
          <Input value={rollbackText} onChange={(e) => setRollbackText(e.target.value)} placeholder="Type ROLLBACK" />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowRollbackConfirm(null); setRollbackText(""); }}>Cancel</Button>
            <Button onClick={() => runRollback(showRollbackConfirm)} disabled={rollbackText !== "ROLLBACK"} className="bg-amber-600 hover:bg-amber-700 disabled:opacity-40">Rollback now</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
